import type { ReactNode } from 'react';
import { Button, Dialog, Heading, Modal, ModalOverlay } from '@launchpad-ui/components';
import { Icon as LaunchpadIcon } from '@launchpad-ui/icons';

import './styles.scss';

export type AlertIntent = 'primary' | 'success' | 'warning' | 'danger' | 'none';

export type AlertProps = {
  /** Whether the alert is open */
  isOpen: boolean;
  /** Called when the alert should close */
  onClose: () => void;
  /** Called when the confirm button is clicked */
  onConfirm?: () => void;
  /** Called when the cancel button is clicked */
  onCancel?: () => void;
  /** Text for the confirm button */
  confirmButtonText?: string;
  /** Text for the cancel button */
  cancelButtonText?: string;
  /** Visual intent of the alert */
  intent?: AlertIntent;
  /** Optional title/header for the alert */
  title?: string;
  /** Optional icon name to display in header */
  iconName?: string;
  /** Alert content */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
};

const getIconForIntent = (intent: AlertIntent, customIcon?: string): 'info' | 'warning' | 'thumb-up-outline' => {
  if (customIcon === 'info' || customIcon === 'warning' || customIcon === 'thumb-up-outline') {
    return customIcon;
  }

  switch (intent) {
    case 'success':
      return 'thumb-up-outline';
    case 'warning':
    case 'danger':
      return 'warning';
    default:
      return 'info';
  }
};

const intentToVariantMap: Record<AlertIntent, 'default' | 'primary' | 'destructive'> = {
  primary: 'primary',
  success: 'primary',
  warning: 'primary',
  danger: 'destructive',
  none: 'primary',
};

export function Alert({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  intent = 'none',
  title,
  iconName,
  children,
  className,
}: AlertProps) {
  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  const icon = getIconForIntent(intent, iconName);
  const confirmVariant = intentToVariantMap[intent];

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal size="small" className={`alert-modal ${className || ''}`}>
        <Dialog>
          {title && (
            <Heading slot="title" className="alert-title">
              <LaunchpadIcon name={icon} />
              <span>{title}</span>
            </Heading>
          )}

          <div className={`alert-content alert-content--${intent}`}>
            {!title && (
              <div className="alert-content__icon">
                <LaunchpadIcon name={icon} />
              </div>
            )}
            <div className="alert-content__message">{children}</div>
          </div>

          <div className="alert-actions">
            {onCancel && (
              <Button variant="default" onPress={handleCancel}>
                {cancelButtonText}
              </Button>
            )}
            {onConfirm && (
              <Button variant={confirmVariant} onPress={handleConfirm}>
                {confirmButtonText}
              </Button>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
